import * as React from "react";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { Selector } from "../DesignSystem/Selector";
import { EntryType } from "../Compo/CompoPage";
import { CompoType } from "../models/compos";
import { api } from "../api";
import { EntryList } from "../DesignSystem/EntryList";
import { EntryJSON } from "../models/entries";
import { Page } from "../DesignSystem/formatting/TwoColumnView";

type TopEntriesParams = {
  user?: string;
};

type CompoChoice = CompoType | "all";

const BASICALLY_INFINITY = 9999;
const ENTRY_CHOICES: EntryType[] = ["song", "art", "story"];
const COMPO_CHOICES: CompoChoice[] = ["all", "main", "day", "hour", "meme"];
const CATEGORY_CHOICES = [
  "overall",
  "theme",
  "atmosphere",
  "sounddesign",
  "uniqueness",
  "production",
  "composition",
  "arrangement",
  "structure",
  "replay",
];

export const TopEntriesPage = (): JSX.Element => {
  const { user } = useParams<TopEntriesParams>();

  const [entryType, setEntryType] = useState<EntryType>("song");
  const [compoType, setCompoType] = useState<CompoChoice>("main");
  const [category, setCategory] = useState<string>("overall");
  const [inTheLast, setInTheLast] = useState<number>(BASICALLY_INFINITY);
  const [sortByLikes, setSortByLikes] = useState<boolean>(false);
  const [entries, setEntries] = useState<EntryJSON[]>([]);

  useEffect(() => {
    let cancelled = false;

    const fetchTopEntries = async () => {
      const response = await api.get("/api/entries/top", {
        params: {
          entryType,
          compoType,
          sortByLikes: String(sortByLikes),
          inTheLast: String(inTheLast),
          category,
          username: user!,
        },
      });

      if (!cancelled) {
        setEntries(response);
      }
    };

    fetchTopEntries();

    return () => {
      cancelled = true;
    };
  }, [entryType, compoType, sortByLikes, inTheLast, category, user]);

  return (
    <Page>
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-slate-800">
        <h1 className="mb-6 text-3xl font-bold text-slate-900 dark:text-white">
          Top Entries {user ? `by ${user}` : ""}
        </h1>

        <div className="space-y-3">
          <Selector
            label="Entry type"
            onChoose={(index) => setEntryType(ENTRY_CHOICES[index])}
            choices={ENTRY_CHOICES}
          />

          <Selector
            label="Sort by"
            onChoose={(index) => setSortByLikes(index === 1)}
            choices={["score", "likes"]}
          />

          <Selector
            label="Compo type"
            onChoose={(index) => setCompoType(COMPO_CHOICES[index])}
            choices={COMPO_CHOICES}
            defaultIndex={1}
          />

          <Selector
            label="Category"
            onChoose={(index) => setCategory(CATEGORY_CHOICES[index])}
            choices={CATEGORY_CHOICES}
          />

          <Selector
            label="Time range"
            defaultIndex={12}
            onChoose={(value) => {
              setInTheLast(value === 12 ? Number(BASICALLY_INFINITY) : Number(value));
            }}
            choices={[
              "1 month",
              "2 months",
              "3 months",
              "4 months",
              "5 months",
              "6 months",
              "7 months",
              "8 months",
              "9 months",
              "10 months",
              "11 months",
              "12 months",
              "Forever",
            ]}
          />
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-slate-800">
        <EntryList entries={entries} />
      </div>
    </Page>
  );
};